/**
 * Estado de autenticação.
 *
 * Guarda o usuário; o access token vive em memória no cliente HTTP e o refresh
 * num cookie httpOnly. Nada de sessão passa por `localStorage`, então um dump
 * do estado (React DevTools, log de erro) não vaza credencial.
 */

import type { UserPrivate } from '@nossorachao/types';
import { create } from 'zustand';

import { api, restoreSession, setAccessToken, setSessionExpiredHandler } from '@/lib/api';
import { resetQueryCache } from '@/lib/query-client';

interface AuthResponse {
  user: UserPrivate;
  tokens: { accessToken: string };
}

interface AuthState {
  user: UserPrivate | null;
  /** true enquanto tenta recuperar a sessão pelo cookie, no boot. */
  initializing: boolean;
  isAuthenticated: boolean;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    acceptMarketing: boolean;
  }) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserPrivate) => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  initializing: true,
  isAuthenticated: false,

  /**
   * Recuperação de sessão ao carregar a página.
   *
   * O access token não sobrevive ao reload (é memória), então o caminho é
   * trocar o cookie por um token novo e só então buscar o usuário. Se o cookie
   * não valer mais, a pessoa cai no login sem ver tela intermediária.
   */
  async bootstrap() {
    const restored = await restoreSession();

    if (!restored) {
      set({ initializing: false, isAuthenticated: false, user: null });
      return;
    }

    try {
      const user = await api.get<UserPrivate>('/auth/me');
      set({ user, isAuthenticated: true, initializing: false });
    } catch {
      setAccessToken(null);
      set({ user: null, isAuthenticated: false, initializing: false });
    }
  },

  async login(email, password) {
    const result = await api.post<AuthResponse>(
      '/auth/login',
      { email, password, device: { platform: 'web', name: navigator.userAgent.slice(0, 60) } },
      { skipAuth: true },
    );

    // Descarta o que estava em cache antes de assumir a nova identidade: se
    // outra conta usou este navegador, os dados dela ainda estão em memória.
    resetQueryCache();
    setAccessToken(result.tokens.accessToken);
    set({ user: result.user, isAuthenticated: true });
  },

  async register(input) {
    const result = await api.post<AuthResponse>(
      '/auth/register',
      {
        name: input.name,
        email: input.email,
        password: input.password,
        ...(input.phone ? { phone: input.phone } : {}),
        acceptedTerms: true,
        acceptedPrivacyPolicy: true,
        acceptMarketing: input.acceptMarketing,
      },
      { skipAuth: true },
    );

    resetQueryCache();
    setAccessToken(result.tokens.accessToken);
    set({ user: result.user, isAuthenticated: true });
  },

  async logout() {
    // Avisa o servidor para revogar a sessão e limpar o cookie, mas não deixa
    // falha de rede prender o usuário logado.
    await api.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    resetQueryCache();
    set({ user: null, isAuthenticated: false });
  },

  setUser(user) {
    set({ user });
  },
}));

// Sessão irrecuperável: o roteador reage e manda para o login.
setSessionExpiredHandler(() => {
  setAccessToken(null);
  resetQueryCache();
  useAuth.setState({ user: null, isAuthenticated: false });
});
